import { ChangeEvent, FormEvent, Fragment, useEffect, useState } from 'react';
import { Button, Col, Container, Form, Modal, Row } from 'react-bootstrap';
import { getFoundationServiceSchemeAndHost } from '../../utils/getFoundationServiceSchemeAndHost';
import axios from 'axios';
import { WorkItemType } from "./dto/WorkItemType";
import { SubjectsDto } from "./dto/SubjectsDto";
import { WorkItemResponseDto } from "./dto/WorkItemResponseDto";
import { SubjectDto } from './dto/SubjectDto';


const UserRequestModal = ({
    isAdmin,
    initialSubjectId,
    isOpen,
    handleClose,
    workItemType,
}: {
    isAdmin: boolean,
    initialSubjectId: string,
    isOpen: boolean;
    handleClose: () => void;
    workItemType: WorkItemType;
}) => {

    const [packages, setPackages] = useState<SubjectDto[]>();
    const [applications, setApplications] = useState<SubjectDto[]>();
    const [validated, setValidated] = useState<boolean>(false);
    const [title, setTitle] = useState<string>('');
    const [severity, setSeverity] = useState<string>('');
    const [subjectId, setSubjectId] = useState<string>(initialSubjectId);
    const [subjectName, setSubjectName] = useState<string>('');
    const [description, setDescription] = useState<string>('');
    const [provEffort, setProvEffort] = useState<string>('');
    const [acceptanceCriteria, setAcceptanceCriteria] = useState<string>('');
    const [isSubmitted, setIsSubmitted] = useState<boolean>(false);

    const profileUrl = `${getFoundationServiceSchemeAndHost()}/profile`;

    useEffect(() => {
        if (packages && applications) return;

        const url = `${getFoundationServiceSchemeAndHost()}/api/subjects`;
        axios.get<SubjectsDto>(url)
            .then(function (response) {
                setApplications(response.data.applications);
                setPackages(response.data.packages);
                // Only the subjectId is passed in as a property. 
                // We need the subject name here in case the user submits the form with the default selection
                setSubjectName(response.data.applications.find(app => app.subjectId === initialSubjectId)?.subjectName ?? '')
            })
            .catch(function (error) {
                console.log(error);
            });

    }, [packages, applications]);

    const handleHide = () => {
        setValidated(false);
        setIsSubmitted(false);
        handleClose();
    }

    const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {

        event.preventDefault();

        const form = event.currentTarget;
        if (!form.checkValidity()) {
            setValidated(true);
            event.stopPropagation();
        }
        else {
            let formData = {
                type: workItemType,
                title,
                severity,
                subjectName: subjectName,
                subjectId: subjectId,
                subjectType: "Application",
                description,
                provEffort,
                acceptanceCriteria,
            };

            const createUrl = `${getFoundationServiceSchemeAndHost()}/api/workitems/create`;


            axios.post<WorkItemResponseDto>(createUrl, formData, {
                headers: {
                    'Content-Type': 'application/json'
                }
            })
                .then(() => {
                    setIsSubmitted(true);
                })
                .catch((error) => {
                    console.error(error);
                });
        }



    };

    const handleTitleChange = (event: ChangeEvent<HTMLInputElement>) => {
        setTitle(event.target.value);
    }
    const handleSeverityChange = (event: ChangeEvent<HTMLInputElement>) => {
        setSeverity(event.target.value);
    }
    const handleSubjectChange = (event: ChangeEvent<HTMLSelectElement>) => {
        setSubjectId(event.target.value);
        setSubjectName(event.target[event.target.selectedIndex].dataset.name ?? "");
    }
    const handleDescriptionChange = (event: ChangeEvent<HTMLInputElement>) => {
        setDescription(event.target.value);
    }
    const handleEffortChange = (event: ChangeEvent<HTMLInputElement>) => {
        setProvEffort(event.target.value);
    }
    const handleAcceptanceCriteriaChange = (event: ChangeEvent<HTMLInputElement>) => {
        setAcceptanceCriteria(event.target.value);
    }

    return (
        <Modal id="work-item-modal" size="lg"
            show={isOpen}
            onHide={handleHide} >
            <Modal.Header closeButton>
                <Modal.Title>
                    {!isSubmitted ? "User Request" : "Thank you!"}
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Container>
                    {!isSubmitted ?
                        <Row>
                            <Col>
                                <h5><small className="text-body-secondary">{workItemType == WorkItemType.Bug ? 'Problem Report' : 'Feature Request'}</small></h5>
                                <Form noValidate validated={validated} onSubmit={handleSubmit}>
                                    <Form.Group className="mb-3" controlId="userRequestForm.Title">
                                        <Form.Label>Title</Form.Label>
                                        <Form.Control aria-label="Title text box"
                                            required
                                            type="text"
                                            placeholder=""
                                            onChange={handleTitleChange} />
                                        <Form.Control.Feedback type="invalid">
                                            Title is required.
                                        </Form.Control.Feedback>
                                    </Form.Group>
                                    <Form.Group className="mb-3" controlId="userRequestForm.Severity">
                                        <Form.Label>{workItemType == WorkItemType.Bug ? 'Severity' : 'Importance'}</Form.Label>
                                        <Form.Control aria-label="Severity selection"
                                            required
                                            as="select"
                                            type="select"
                                            onChange={handleSeverityChange}>
                                            <option value="">Select</option>
                                            <option value="critical">Critical</option>
                                            <option value="high">High</option>
                                            <option value="medium">Medium</option>
                                            <option value="low">Low</option>
                                        </Form.Control>
                                        <Form.Control.Feedback type="invalid">
                                            Selection is required.
                                        </Form.Control.Feedback>
                                    </Form.Group>
                                    <Form.Group className="mb-3" controlId="userRequestForm.Subject">
                                        <Form.Label>Application</Form.Label>
                                        <Form.Select aria-label="Application selection"
                                            value={subjectId}
                                            required
                                            as="select"
                                            onChange={handleSubjectChange}>
                                            {applications &&
                                                <optgroup label="Applications">
                                                    {
                                                        applications.map((subject: SubjectDto, index: number) => {
                                                            return <option key={index} data-name={subject.subjectName} value={subject.subjectId}>{subject.subjectName}</option>;
                                                        })
                                                    }
                                                </optgroup>
                                            }
                                            {packages &&
                                                <optgroup label="Packages">
                                                    {
                                                        packages.map((subject: SubjectDto, index: number) => {
                                                            return <option key={index} data-name={subject.subjectName} value={subject.subjectId}>{subject.subjectName}</option>;
                                                        })
                                                    }
                                                </optgroup>
                                            }
                                        </Form.Select>
                                        <Form.Control.Feedback type="invalid">
                                            Selection is required.
                                        </Form.Control.Feedback>
                                    </Form.Group>
                                    <Form.Group className="mb-3" controlId="userRequestForm.Description">
                                        <Form.Label>Description</Form.Label>
                                        <Form.Control aria-label="Description text area"
                                            required
                                            as="textarea"
                                            rows={3}
                                            placeholder="Please provide a detailed description."
                                            onChange={handleDescriptionChange} />
                                        <Form.Control.Feedback type="invalid">
                                            Description is required.
                                        </Form.Control.Feedback>
                                    </Form.Group>
                                    {isAdmin &&
                                        <Fragment>
                                            <Form.Group className="mb-3" controlId="userRequestForm.Effort">
                                                <Form.Label>Estimated Effort</Form.Label>
                                                <Form.Control aria-label="Estimated effort selection"
                                                    as="select"
                                                    type="select"
                                                    onChange={handleEffortChange}>
                                                    <option value="">Select</option>
                                                    <option value="1">1</option>
                                                    <option value="2">2</option>
                                                    <option value="3">3</option>
                                                    <option value="5">5</option>
                                                    <option value="8">8</option>
                                                </Form.Control>
                                            </Form.Group>
                                            <Form.Group className="mb-3" controlId="userRequestForm.AcceptanceCriteria">
                                                <Form.Label>Acceptance Criteria</Form.Label>
                                                <Form.Control aria-label="Acceptance criteria text area"
                                                    as="textarea"
                                                    rows={3}
                                                    onChange={handleAcceptanceCriteriaChange} />
                                            </Form.Group>
                                        </Fragment>
                                    }

                                    <Button variant="primary" type="submit">
                                        Submit
                                    </Button>
                                </Form>
                            </Col>
                        </Row> :
                        <Fragment>
                            <p>Your request has been successfully submitted. Thank you for taking the time. Your feedback helps us develop the best quality product.</p>
                            <p> You may track the status of all requests on your <a href={profileUrl} target="_blank" rel="noopener noreferrer">Profile page</a>.</p>
                        </Fragment>
                    }
                </Container>
            </Modal.Body>
        </Modal>
    );
};


export default UserRequestModal;


